import {
  EntrySortingType,
  hasPriorityEntries,
  NotificationType,
  useLorebook,
  useLorebookEntries,
  useLorebookStagedEdits,
  useTranslation,
} from "@next-platform/client-common-core";
import { Spinner } from "@next-platform/client-common-spicy-ui";
import { LorebookStatus, LorebookVisibility, ModerationStatus, PostLorebookRequest } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import { FEATURE_FLAGS, ROUTES } from "@/constants";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useToast from "@/hooks/useToast";

import LorebookForm, { ILorebookSubmitError, TabNames } from "./components/LorebookForm";
import { DEFAULT_LOREBOOK_VISIBILITY, FORM_FIELDS } from "./constants";
import { ILorebookDataFormProps, ILorebookEditNavigationState } from "./types";

interface ILorebookEditProps {
  id: string;
}

export default function LorebookEdit({ id }: ILorebookEditProps) {
  // useLorebookEntries owns lorebook metadata + the server-paginated, server-searched entries list.
  // useLorebook is used only for the details-tab update mutation.
  const { onUserUpdateLorebook, onUserSubmitLorebookModeration } = useLorebook();
  const {
    lorebook,
    entries,
    isLoadingFirstPage,
    isLoadingMore,
    hasMore,
    hasError,
    searchValue,
    setSearchValue,
    loadNextPage,
    reload,
    reloadFromStart,
  } = useLorebookEntries(id);
  // Local staging session for the public-lorebook moderation flow. Entry + parent edits accumulate
  // here (live data untouched) until the user submits the aggregated snapshot for review.
  const { pendingLorebookData, hasStagedChanges, stagedOpByEntryId, stageEntryCreate, stageEntryUpdate, stageEntryDelete, resetStagedEdits } =
    useLorebookStagedEdits();
  const { hasFeatureFlag: isEditModerationEnabled } = useFeatureFlag(FEATURE_FLAGS.LOREBOOK_PUBLIC_EDIT_MODERATION);
  // Staging applies only to already-PUBLIC lorebooks when the moderation flag is on; everything else
  // keeps the legacy immediate-API flow. Mirrors the backend `shouldStageLorebookEdit` gate.
  const isStagingMode = isEditModerationEnabled && lorebook?.visibility === LorebookVisibility.PUBLIC;
  // Must match LorebookForm's isActionRequired, which also accepts the nested moderation report.
  const isActionRequired =
    lorebook?.moderationReportStatus === ModerationStatus.FixRequired ||
    lorebook?.moderationReport?.moderation_status === ModerationStatus.FixRequired;
  const [stateIsLoading, setStateIsLoading] = useState(false);
  // Save error surfaced inline above the submit CTA (e.g. a moderation/underage rejection when
  // publishing a Public lorebook) instead of as a transient toast, per design (NX-11368). Tagged
  // with the tab that submitted, since tabs stay clickable while the save is in flight.
  const [stateSubmitError, setStateSubmitError] = useState<ILorebookSubmitError | null>(null);
  // Entries-tab sort. Priority is the server's own ordering: every entries request sends
  // sortBy=priority and the backend already returns the entries failing the entry requirements
  // first, so Priority needs no refetch and no client re-sort — which is also what keeps it correct
  // across every page of a 5,000-entry lorebook rather than only the pages loaded so far. The other
  // options reorder the entries already loaded.
  const [stateSortType, setStateSortType] = useState<EntrySortingType>(EntrySortingType.PRIORITY);
  // Only the newest save may write state: a slow response can land after a newer save, or after
  // `id` has moved to another lorebook.
  const refLatestSaveId = useRef(0);
  const { displayToast } = useToast();
  const { t } = useTranslation();
  const { pagePath } = useLocalizedLocation();
  const navigate = useNavigate();
  const isEntriesTab = /\/entries\b/.test(pagePath);
  const refWasEntriesTab = useRef(isEntriesTab);
  // Visibility the user chose at create time, handed off via navigation state. Captured once so it
  // pre-selects the form on first render (the lorebook is created Private) without re-applying on
  // later refetches or tab switches, which would fight the user's own edits.
  const location = useLocation();
  const refIntendedVisibility = useRef((location.state as ILorebookEditNavigationState | null)?.intendedVisibility);
  const initialVisibility: LorebookVisibility = refIntendedVisibility.current ?? lorebook?.visibility ?? DEFAULT_LOREBOOK_VISIBILITY;

  useEffect(() => {
    window.scrollTo(0, 0);
    // LorebookEdit stays mounted when navigating between lorebooks, so clear any stale save error
    // from the previous lorebook when the id changes — otherwise it would show while editing another.
    setStateSubmitError(null);
    // Clearing is not enough: an in-flight save for the previous lorebook would re-set the error
    // on resolution. Bump to retire it.
    refLatestSaveId.current += 1;
    setStateIsLoading(false);
    // Likewise reset the sort: it is a per-lorebook view preference, and the component stays mounted
    // across lorebooks, so a Latest/Name choice made on the previous one would otherwise keep
    // reordering the newly loaded entries.
    setStateSortType(EntrySortingType.PRIORITY);
    // Drop any staged edits from the previous lorebook: the session is per-lorebook, and the
    // component stays mounted across lorebooks, so leftover entry_ops would otherwise overlay the new
    // list and could be submitted against the wrong lorebook id.
    resetStagedEdits();
  }, [id, resetStagedEdits]);

  // Refetch only when transitioning INTO the Entries tab so newly imported or moderated
  // entries appear without requiring a manual page refresh (NX-9036). Leaving the tab must
  // not trigger a fetch or reset scroll position.
  // Use reloadFromStart (not reload) so the refetch is unfiltered: reload() reuses the persisted
  // search, which would (a) hide newly imported/moderated entries that don't match the query,
  // defeating NX-9036, and (b) skip refreshing num_entries and the moderation counts (search
  // responses are ignored for lorebook metadata). Clearing the filter restores the original
  // unfiltered getLorebookData(id) behavior.
  useEffect(() => {
    const shouldRefetchOnEntriesEnter = isEntriesTab && !refWasEntriesTab.current;
    refWasEntriesTab.current = isEntriesTab;
    if (shouldRefetchOnEntriesEnter) {
      reloadFromStart();
    }
  }, [id, isEntriesTab, reloadFromStart]);

  const updateLorebook = useCallback(
    async (formData: FieldValues, submittingTab: TabNames) => {
      refLatestSaveId.current += 1;
      const saveId = refLatestSaveId.current;
      // LorebookEdit stays mounted across lorebooks, so pin the target id for this request.
      const savedForId = id;
      setStateIsLoading(true);
      setStateSubmitError(null);
      try {
        const payload = {
          name: formData.name || "",
          description: formData.description,
          tags: formData.tags,
          avatar_url: formData.avatar_url,
          visibility: formData[FORM_FIELDS.VISIBILITY],
        } as PostLorebookRequest;
        const response = await onUserUpdateLorebook(savedForId, payload);
        // Superseded while in flight: applying this would show an error over a lorebook that saved
        // fine, or over a different lorebook entirely.
        if (saveId !== refLatestSaveId.current) {
          return;
        }
        if (response.data) {
          // Refresh unfiltered (reloadFromStart, not reload): the just-saved name/description/tags/
          // visibility only land in the cached lorebook via a non-search response, so an active
          // entries-tab search would otherwise leave that metadata stale after saving.
          reloadFromStart();
          displayToast({ message: "Lorebook updated", type: NotificationType.default });
        } else if (response.error) {
          // Tagged with the submitting tab so the message stays on the tab the user saved from,
          // even if they switched tabs while the request was in flight (NX-11368).
          setStateSubmitError({ message: response.error, tab: submittingTab });
        }
      } finally {
        // A superseded save must not clear the spinner owned by the request that replaced it.
        if (saveId === refLatestSaveId.current) {
          setStateIsLoading(false);
        }
      }
    },
    [id, onUserUpdateLorebook, displayToast, reloadFromStart],
  );

  // Staged-edit submit: bundle the parent form fields plus the aggregated entry_ops into one
  // moderation snapshot (PATCH /lorebooks/{id}). The backend stages it as pending_lorebook_data and
  // leaves live data untouched until an admin approves. Only used when isStagingMode is on.
  const submitModeration = useCallback(
    async (formData: FieldValues, submittingTab: TabNames) => {
      refLatestSaveId.current += 1;
      const saveId = refLatestSaveId.current;
      const savedForId = id;
      setStateIsLoading(true);
      setStateSubmitError(null);
      try {
        const payload = {
          name: formData.name || "",
          description: formData.description,
          tags: formData.tags,
          avatar_url: formData.avatar_url,
          visibility: formData[FORM_FIELDS.VISIBILITY],
          // entry_ops omitted entirely when nothing is staged (backend rejects an empty array).
          ...(pendingLorebookData.entry_ops ? { entry_ops: pendingLorebookData.entry_ops } : {}),
        };
        const response = await onUserSubmitLorebookModeration(savedForId, payload);
        if (saveId !== refLatestSaveId.current) {
          return;
        }
        if (response.data) {
          // The edits are now pending review, so drop the local session and return the user to My
          // Creations rather than leaving them on an edit form that is now locked under review.
          resetStagedEdits();
          displayToast({ message: t("lorebook:toast.submittedForReview"), type: NotificationType.default });
          navigate(ROUTES.myCreationLorebook);
        } else if (response.error) {
          setStateSubmitError({ message: response.error, tab: submittingTab });
        }
      } finally {
        if (saveId === refLatestSaveId.current) {
          setStateIsLoading(false);
        }
      }
    },
    [id, onUserSubmitLorebookModeration, pendingLorebookData, resetStagedEdits, displayToast, t, navigate],
  );

  const onSubmit = useCallback(
    (formData: FieldValues, submittingTab: TabNames) => {
      // Failures are surfaced inline (stateSubmitError) inside the submit handlers; swallow here to
      // satisfy the void handler.
      const isSubmittingPublic = formData[FORM_FIELDS.VISIBILITY] === LorebookVisibility.PUBLIC;
      // The backend can't change visibility and apply staged entry_ops together, and updateLorebook
      // would silently drop them — so block a to-private save while entry ops are staged.
      if (isStagingMode && !isSubmittingPublic && hasStagedChanges) {
        setStateSubmitError({ message: t("lorebook:error.stagedEntriesBeforePrivate"), tab: submittingTab });
        return;
      }
      const submit = (isStagingMode && isSubmittingPublic) || isActionRequired ? submitModeration : updateLorebook;
      submit(formData, submittingTab).catch(() => undefined);
    },
    [isStagingMode, isActionRequired, hasStagedChanges, submitModeration, updateLorebook, t],
  );

  // Dismiss the inline save error once the user edits the form, so a stale moderation message does
  // not linger after they act on it. Switching tabs does not clear it: the error is tab-tagged and
  // simply stops rendering while another tab is active.
  const clearSubmitError = useCallback(() => setStateSubmitError(null), []);

  // Refresh from the unfiltered first page (not reload(), which reuses the active search query).
  // A create/delete changes num_entries, and only a non-search response refreshes that cached
  // total, so an active search filter would otherwise leave counts and publish gates stale.
  const handleEntryMutated = useCallback(
    (entryCountDelta?: number) => {
      reloadFromStart(entryCountDelta);
    },
    [reloadFromStart],
  );

  const handleSortChange = useCallback((next: EntrySortingType) => {
    setStateSortType(next);
  }, []);

  // The initial fetch can fail before any lorebook is cached, leaving the component to render nothing
  // (return null below). Surface that failure via a toast so the user gets feedback instead of a blank
  // screen. Guard on `!lorebook` so this only fires for the initial load, not a later refetch error.
  useEffect(() => {
    if (hasError && !lorebook) {
      displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
    }
  }, [hasError, lorebook, displayToast, t]);

  if (isLoadingFirstPage && !lorebook) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner className="mx-auto" />
      </div>
    );
  }

  if (!lorebook) {
    return null;
  }

  const numRejectedEntries = lorebook.num_rejected_entries || 0;
  const numNeedsReviewEntries = lorebook.num_needs_review_entries || 0;
  // Priority is the default sort, but with no flagged entries it is not a selectable option. Fall
  // back to Latest so both the dropdown label and the actual entry ordering (sortEntries) agree —
  // otherwise the control reads "Latest" while entries stay in server priority order.
  const isPrioritySortAvailable = hasPriorityEntries(
    numRejectedEntries,
    numNeedsReviewEntries,
    lorebook.num_flagged_entries || 0,
    lorebook.pending_ops_summary?.needs_fix ?? 0,
  );
  const effectiveSortType = stateSortType === EntrySortingType.PRIORITY && !isPrioritySortAvailable ? EntrySortingType.LATEST : stateSortType;

  return (
    <div className="flex flex-col gap-lg">
      <LorebookForm
        isProcessingEntries={lorebook?.status === LorebookStatus.IN_MODERATION}
        onSubmit={onSubmit}
        isLoading={stateIsLoading}
        submitError={stateSubmitError}
        onClearSubmitError={clearSubmitError}
        status={lorebook.status}
        numRejectedEntries={numRejectedEntries}
        numNeedsReviewEntries={numNeedsReviewEntries}
        onEntryMutated={handleEntryMutated}
        isStagingMode={isStagingMode}
        hasStagedChanges={hasStagedChanges}
        stagedOpByEntryId={stagedOpByEntryId}
        onStageEntryCreate={stageEntryCreate}
        onStageEntryUpdate={stageEntryUpdate}
        onStageEntryDelete={stageEntryDelete}
        onDiscardStagedEdits={resetStagedEdits}
        initialValues={
          {
            [FORM_FIELDS.NAME]: lorebook.name,
            [FORM_FIELDS.DESCRIPTION]: lorebook.description,
            // [FORM_FIELDS.GUIDELINES]: false,
            [FORM_FIELDS.ENTRIES]: lorebook.entries || [],
            [FORM_FIELDS.AVATAR]: lorebook.avatar_url,
            [FORM_FIELDS.TAGS]: lorebook.tags || [],
            [FORM_FIELDS.VISIBILITY]: initialVisibility,
          } as unknown as ILorebookDataFormProps
        }
        totalEntries={lorebook.num_entries}
        lorebookData={lorebook}
        entries={entries}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        hasMore={hasMore}
        isLoadingFirstPage={isLoadingFirstPage}
        isLoadingMore={isLoadingMore}
        hasError={hasError}
        onLoadMore={loadNextPage}
        onRetry={reload}
        sortType={effectiveSortType}
        onSortChange={handleSortChange}
      />
    </div>
  );
}
